/**
 * What a surface draws while it waits, instead of the word „Ładowanie…".
 *
 * Owner decision, 18 September 2026: of the five animations in
 * `docs/design/2026-09-18-loading-animations-mockup.html` the app ships variant **B, "Pulse"** — a
 * faint print of the brand mark with ink running through it, holding, and draining out. (E, "Rep",
 * was picked first and withdrawn the same day as too busy.) It fades in only after a grace, so a
 * fast read draws nothing. The geometry, the timings and the gate are in `BrandLoader.module.css`;
 * this file is the markup and nothing else.
 *
 * ── The word does not disappear, it stops being DRAWN ───────────────────────────────────────
 * `label` is the surface's existing translated status sentence and it is rendered visually hidden,
 * so the announcement a screen reader makes is byte-identical to the one it made before. That is
 * also why the element itself carries `role="status"` and both marks are `aria-hidden`: there is
 * one live region here, and it says a sentence rather than describing an animation.
 *
 * ── Why `shared/`, on the first commit ──────────────────────────────────────────────────────
 * The promote-on-the-second-consumer rule is about a thing that MIGHT be reused. This one has
 * eight call sites the day it is written — the session boundary, both calendars, the roster, the
 * builder, the archive, the assign dialog and the session overlay — and a per-slice copy of the
 * band, its inverse and the gate is exactly the duplication that rule exists to prevent.
 */

import brandMark from '~/shared/ui/brandMark.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import styles from './BrandLoader.module.css';

interface BrandLoaderProps {
  /**
   * The surface's own translated waiting sentence. Visually hidden, never omitted — an animation
   * with no accessible name is a busy indicator that says nothing.
   */
  readonly label: string;
  /**
   * `screen` for a whole viewport or a whole canvas, `panel` for a card, a dialog or the session
   * panel's slot. Required rather than defaulted: which one a surface is, is a decision the
   * surface makes.
   */
  readonly size: 'screen' | 'panel';
}

export function BrandLoader({ label, size }: BrandLoaderProps) {
  return (
    <div
      className={[styles.loader, size === 'screen' ? styles.screen : styles.panel].join(' ')}
      role="status"
    >
      <span className={[brandMark.brandMark, styles.mark, styles.ghost].join(' ')} aria-hidden />
      <span className={styles.band} aria-hidden>
        <span className={[brandMark.brandMark, styles.mark, styles.ink].join(' ')} />
      </span>
      <span className={hidden.srOnly}>{label}</span>
    </div>
  );
}
